// Query-string builders for the v1 Audit Queue endpoints.
//
// Wire formats:
//   • Paginated list   → ?page&pageSize&sort=field:dir&filter=field:op:csv&search + myAudits
//   • Aggregates       → plain typed query params (kpis, urgency)
//   • Typeahead        → search + limit + myAudits + the grid's other ?filter= (cascading)

import {
  AuditListParams,
  AuditKpiParams,
  AuditUrgencyParams,
  AuditTypeaheadParams,
  AuditFilterField,
  AuditFilterSelection,
} from "../types";

const enc = encodeURIComponent;

// Map internal filter field names to API field names. Exported because callers
// that build a query string by hand — MyAuditsView, via audit-queue-api's
// URLSearchParams helpers — need the same translation the wire format uses here.
export const FILTER_FIELD_API_NAMES: Record<AuditFilterField, string> = {
  'statusFilter': 'Status',
  'auditGroupFilter': 'AuditGroup',
  'outcomeFilter': 'Outcome',
  'qaReviewerFilter': 'QAReviewer',
  'auditTypeFilter': 'AuditType',
  'auditorFilter': 'Auditor',
  'auditPeriodFilter': 'AuditPeriod',
};

const FILTER_FIELDS = Object.keys(FILTER_FIELD_API_NAMES) as AuditFilterField[];

// Build ?filter=Field:in:csv clauses from a selection. Shared by the list and
// the typeaheads: both speak the same grammar and are validated against the
// same server-side schema, and the typeaheads cascade on the list's selection.
// `exclude` drops one dimension — a typeahead never filters on the field it is
// itself searching.
const buildFilterClausesFrom = (
  filters: AuditFilterSelection,
  exclude?: AuditFilterField
): string[] =>
  FILTER_FIELDS
    .filter(field => field !== exclude)
    .flatMap(field => {
      const values = filters[field];
      if (!values || !Array.isArray(values) || values.length === 0) return [];
      // URL encode each value separately to handle special characters
      const encodedValues = values.map(v => enc(String(v))).join(',');
      return [`filter=${FILTER_FIELD_API_NAMES[field]}:in:${encodedValues}`];
    });

// Build filter clauses for the list endpoint using ?filter=field:in:csv format
const buildFilterClauses = (params: AuditListParams): string[] =>
  buildFilterClausesFrom(params as AuditFilterSelection);

// /audits — paginated list
export const buildListQuery = (params: AuditListParams): string => {
  const parts = [
    `page=${params.page}`,
    `pageSize=${params.pageSize}`,
    ...buildFilterClauses(params),
  ];

  if (params.sortBy && params.sortDir) {
    parts.push(`sort=${enc(params.sortBy)}:${params.sortDir}`);
  }

  if (params.scoreType) {
    parts.push(`scoreType=${enc(params.scoreType)}`);
  }

  if (params.search) {
    parts.push(`search=${enc(params.search)}`);
  }

  if (params.myAudits) {
    parts.push(`myAudits=${params.myAudits}`);
  }

  // Facets are the most expensive part of the query, so the framework defaults
  // them off and an individual call opts in. Without this the response carries
  // "facets": [] and the Status / Audit Group / Outcome / QA Reviewer dropdowns
  // have nothing to render.
  if (params.includeFacets) {
    parts.push("includeFacets=true");
  }

  return parts.join("&");
};

// /audits/kpis — header KPI cards (myAudits only)
export const buildKpiQuery = (params: AuditKpiParams): string => {
  const parts: string[] = [];
  if (params.myAudits) {
    parts.push(`myAudits=${params.myAudits}`);
  }
  return parts.join("&");
};

// /audits/urgent — urgency list (configurable ranks)
export const buildUrgencyQuery = (params: AuditUrgencyParams): string => {
  const parts: string[] = [];

  if (params.myAudits) {
    parts.push(`myAudits=${params.myAudits}`);
  }
  if (params.overdueRank !== undefined) {
    parts.push(`overdueRank=${params.overdueRank}`);
  }
  if (params.dueSoonRank !== undefined) {
    parts.push(`dueSoonRank=${params.dueSoonRank}`);
  }
  if (params.stalledRank !== undefined) {
    parts.push(`stalledRank=${params.stalledRank}`);
  }
  if (params.stalledDays !== undefined) {
    parts.push(`stalledDays=${params.stalledDays}`);
  }
  if (params.topN !== undefined) {
    parts.push(`topN=${params.topN}`);
  }

  return parts.join("&");
};

// Typeahead endpoints — /types, /auditors, /periods
export const buildTypeaheadQuery = (params: AuditTypeaheadParams): string => {
  const parts = [
    `search=${enc(params.search)}`,
    `limit=${params.limit ?? 20}`,
  ];

  if (params.myAudits) {
    parts.push(`myAudits=${params.myAudits}`);
  }

  // Cascade (ADR 0005): the grid's other active filters narrow the option list
  // and its counts. The endpoint self-excludes its own dimension, so that one
  // is left out here.
  parts.push(...buildFilterClausesFrom(params.filters ?? {}, params.excludeField));

  return parts.join("&");
};
